import { createMachine, assign } from 'xstate';

import OrganizationApi from 'entities/organization/api';
import UserApi from 'entities/user/api';
import type Organization from 'entities/organization/model';
import type { OnboardingStep } from 'entities/user/model';

import { Brand } from './components/Brand';
import { Contact } from './components/Contact';
import { Presence } from './components/Presence';

const makeSaveNonprofitData =
  (onboardingDataValidationCompleted = false) =>
  (context: DataCaptureMachineContext, event: SubmitEvent): Promise<void> =>
    new Promise((resolve, reject) => {
      if (event.type === 'SUBMIT') {
        OrganizationApi.update(
          context.organization.id,
          onboardingDataValidationCompleted
            ? {
                ...event.data,
                onboardingDataValidationCompleted: true,
              }
            : event.data
        ).subscribe(
          (action) => {
            let { organization } = context;

            // @NOTE-DP: we can't just set whatever `OrganizationApi.update` returns, since its response is not populated
            // So instead let's take the current `user.organization` state and override only the things which we changed
            Object.keys(event.data).forEach((key) => {
              organization = organization.set(key, event.data[key]) as Organization;
            });

            // @NOTE-DP: since `nonprofitDataValidatedAt` is not returned, let's just fake it to something truthy, so the tracker would work without reloading the UI
            if (onboardingDataValidationCompleted) {
              organization = organization.set(
                'nonprofitDataValidatedAt',
                new Date()
              ) as Organization;
            }

            context.dispatch({
              ...action,
              organization,
            });
            resolve();
          },
          (error) => reject(error)
        );
      }
    });

export interface DataCaptureMachineContext {
  dispatch: (action: any) => void;
  organization: Organization;
  dismissedSteps?: Record<OnboardingStep, boolean>;
  allowedSteps?: OnboardingStep[];
  privileges: string[];
  error: null | Error;
}

interface SubmitEvent {
  type: 'SUBMIT';
  data: Record<string, unknown>;
}

export type DataCaptureMachineEvents =
  | SubmitEvent
  | { type: 'PERSIST_LETTER'; governmentalInformationLetterUri: string }
  | { type: 'PREV' }
  | { type: 'NEXT' }
  | { type: 'ENROLL'; location: string }
  | { type: 'DISMISS' }
  | { type: 'NAVIGATE'; location: string }
  | { type: 'RETRY' }
  | { type: 'SKIP' };

export const dataCaptureMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QQIYBcUGEUAc0FcAnMAOgEsA7MtMlAGwGIBtABgF1FQcB7Was7hU4gAHogCMAFgCcJAMwAmABzSWclgoDsANgUBWOdoA0IAJ6JpSkiz2q9kzUvGabhgL5uTqDNjxFSlPz0zOIcSCA8fDSCwmII4nKyCpIs2iqazqksmpom5ggK0pLySgraOhl6GWV6Hl7oWLgExORUNMFMCmFcvPwx4XHietokSmoK487SimV5iPYsJHoKcnri0nprTixKdSDejX4tAKoAynos4gDGcrDM7MKRfUIDEuLvo4qrDpJKekppOYIdSaEiaaYaHYVNKaPYHXzNUhnC7XW6tIKMU7HABCAFkAJIAFVY3QivWiL1AcWWIyUfwBCnEhUkKyBK3E8nWKhkqV04m0kjhDQR-hIyMuN1gJFg+AARgBbag0ChQBgQQQBCgAN24AGtSPCmqLxaipTKFUrKFAEJQdVd0AIKCSSY9yY7YhZNMVlixUmtHMpJHogZJJBzpMppC4gylxH8hT4jSdzhK0ebFWhlaqwIRCNxCCQcHR0AAzfPykiGo5IlOm6VyjNZm3a7j2inOh7hJ4Uj0IDIjVTM-nSbQsCO5MyIFKyZyJL0acECoMJw6IsW1yUkEsoMh0fwMLF4okurtu-pU+YTaz6Uf8mT85RA1ZyeSaTZlUNRkdaFcilo4YhYDACgrjAe5SW7d1XniJkrGUAUoQmIY5CUIFpg5X05HBZ8JlKaRfyTUgALgYDQPRdpMRxAliU7Hooigi8EFsBQwQFOQ5DDHQlA4yQgVKFjhg4hIoSUFJYU8fZhUIwtANI0h00tFU1Q1VodX1SspOrGSSJA+SG0U61bVbB1BA7CCz0pUQLCqT4BX0C4R1Eid8nEcYSEsZZ1n0Qx-nE+pEy04igN0+sLUzK0GBzPMCyLUtyw0gK1yCuTQsbK1mztEynXYE86OeXtmNYyR2M4tIeKBTRkiWbRnxSX0VhkAjAtkkLt13fdD2o3KyXo88rKYiMipK5wytDCrKusGrQyGX45C8pq1yuQQMCuNBwNdXrLMGBIX3eINEg48phmMSd4hQ0FipUAElEcaR3gUBbRSWigVrQcjaEoo8aPMzbe1HKxfSqbQ4z+ZxUNOnaWNDHYVjSTI5Eelpnte1KDOUihNTUg1NMW5aUFW1HwpVDLjPbHLaJ6-LoJHUEUIBDQNjDd5pCBIZUhId4ciqHJfgjfCJKrXGXvxt6FKJ7Nc3zQtizQMtCArQWnrxgmxabIy20dMyNqpxj-usGwdBB-1wZchqSGhjIxk5uMHoFnGleFgm2r3YgDyo48Kcgvq4j1wHDbpY3WeUcMxLphyI3ERHSFlQgUAoCB1tPX7oKZIpzcq0dJDSS52JNiRSnDLCEi9bZlFt-zV1FGO44gd7gk6j2fp1-rgZfFxnGGfkLmyFmIbpFio00L5mbGBG7cSqvY-jwms3RzG9WxieWmr6fVfS9Wsq1pPm+2nIllm4ZP2KyxWZUDCbu0bmEjEvzJKX6Op9rtelKiqXYtl+LFeXx+Z-XlsNdMuTJuPZoKtzBJcN8wNL6+nBKzRwxRB6Mi9DIJwuxx6V2-jXLcO4XZgQbt9bWIDdYJHAR3KB3dYF90UOnSqKFIxjgULbCSFBuAQDgMIL+YBCEMX6gAWhOi5UYXpVBjjUFGd4Y40EVz-JqDE3DvZTgUHxWQWEeRFBQhcYYtR0EyPXCiSU8itqICwiMHuRRCg0l+PoVmKRzY93BMsbujDtHSOkiaTcgQKKGN7CyYoZiWQbA-KUYMp12ScksHQ4GGg0hRz0amM0+lxbeNAeCEgjCtChnproaQvd8iMNBKoK2Q9fSXzmrE9xaJnb+GScQ4oAIOJRn0DYf4AipxSGsJVRk2Qs7OB0LE5Kukan9RQosJwTJZw1W0AKVpCBdB6HclbBIhRGH81cc1HSZFPEfSGXEGQxQplaCjNzVwuTEClBfKkIS6gbqiWyP0lqZFn5QB2WcoY5tj6OGfJ5cQFUULVQ4sMDQYZRyrLvhgoiDzSBVOIC8hALIrBjKQYYQw0yKokN+DnOMt41C304SQZGItYWJFMZYi4lVfSlFZgkeZfwh53QyOoSwsSCUEy2fQWFDl3IsBZPoZQBgjms3YgPFI5y4YuDHmsoWKMnmwv4ubNYqReV3SqLxCGyQrDQzGDkyq8LcX2yRsrN60KuHbyIf1UMfjSXZAmDDIOKx5AqtcsoMc7xtCxJXhAWFsYwS-BqtMLYIyQkuTSCMA6Z8hjMxyO6n+bK6AcreaODQ6wtH5LkKfbiNDh5cjUNGrBMrTU8LiE4eZoZEgsnUOoUMQaJDgisIPRlYYbDKD1ffEgHrsHtRhQWhRcKZA+vqf6uMga4FD1GNMVQ4jVB7VieqDGsL+Q7CWIzKM7FSkoTZJcYos0ajBPhh4DwQA */
    id: 'dataCapture',
    key: 'dataCapture',
    initial: 'initial',
    schema: {
      context: {} as DataCaptureMachineContext,
      events: {} as DataCaptureMachineEvents,
      services: {} as {
        saveNonprofitData: {
          data: void;
        };
        saveNonprofitDataWithTimestamp: {
          data: void;
        };
      },
    },
    states: {
      initial: {
        always: [{ target: 'presence' }],
      },
      presence: {
        initial: 'initial',
        always: [
          {
            target: 'contact',
            cond: 'presenceCompleted',
          },
        ],
        states: {
          initial: {
            on: {
              SUBMIT: 'submitting',
              NEXT: '#dataCapture.contact',
            },
          },
          submitting: {
            entry: assign({
              error: () => null,
            }),
            invoke: {
              src: 'saveNonprofitData',
              onDone: {
                target: '#dataCapture.contact',
                actions: ['persistPresenceData'],
              },
              onError: {
                target: 'failure',
              },
            },
          },
          failure: {
            entry: assign({
              error: (_, event) => {
                if ('data' in event && event.data instanceof Error) {
                  return event.data;
                }
                return new Error('Submission failed');
              },
            }),
            on: {
              SUBMIT: 'submitting',
            },
          },
        },
        meta: {
          component: Presence,
        },
      },
      contact: {
        initial: 'initial',
        always: [
          {
            target: 'brand',
            cond: 'contactCompleted',
          },
        ],
        states: {
          initial: {
            on: {
              SUBMIT: 'submitting',
              // @NOTE-EV: should only be available to super admins
              PREV: '#dataCapture.presence',
              NEXT: '#dataCapture.brand',
            },
          },
          submitting: {
            entry: assign({
              error: () => null,
            }),
            invoke: {
              src: 'saveNonprofitData',
              onDone: {
                target: '#dataCapture.brand',
                actions: ['persistContactData'],
              },
              onError: {
                target: 'failure',
              },
            },
          },
          failure: {
            entry: assign({
              error: (_, event) => {
                if ('data' in event && event.data instanceof Error) {
                  return event.data;
                }
                return new Error('Submission failed');
              },
            }),
            on: {
              SUBMIT: 'submitting',
            },
          },
        },
        meta: {
          component: Contact,
        },
      },
      brand: {
        initial: 'initial',
        always: [
          {
            target: 'done',
            cond: 'brandCompleted',
          },
        ],
        states: {
          initial: {
            on: {
              SUBMIT: 'submitting',
              // @NOTE-EV: should only be available to super admins
              PREV: '#dataCapture.contact',
              NEXT: '#dataCapture.done',
            },
          },
          submitting: {
            entry: assign({
              error: () => null,
            }),
            invoke: {
              src: 'saveNonprofitDataWithTimestamp',
              onDone: {
                target: '#dataCapture.done',
                actions: ['persistBrandData'],
              },
              onError: {
                target: 'failure',
              },
            },
          },
          failure: {
            entry: assign({
              error: (_, event) => {
                if ('data' in event && event.data instanceof Error) {
                  return event.data;
                }
                return new Error('Submission failed');
              },
            }),
            on: {
              SUBMIT: 'submitting',
            },
          },
        },
        meta: {
          component: Brand,
        },
      },
      done: {
        type: 'final',
        entry: ['handleFinal'],
      },
    },
    tsTypes: {} as import('./machine.typegen').Typegen0,
  },
  {
    actions: {
      persistPresenceData: (context) => {
        if (!context.dismissedSteps?.presence) {
          UserApi.update({
            onboarding: {
              presence: true,
            },
          });
        }
      },
      persistContactData: (context) => {
        if (!context.dismissedSteps?.contact) {
          UserApi.update({
            onboarding: {
              contact: true,
            },
          });
        }
      },
      persistBrandData: (context) => {
        if (!context.dismissedSteps?.brand) {
          UserApi.update({
            onboarding: {
              brand: true,
            },
          });
        }
      },
    },
    // @TODO-DP: utilise `getAllowedSteps` here as well
    guards: {
      presenceCompleted: (context, _event, { state }) =>
        Boolean(context.dismissedSteps?.presence) && (state.event?.type as string) !== 'PREV',
      contactCompleted: (context, _event, { state }) =>
        Boolean(context.dismissedSteps?.contact) && (state.event?.type as string) !== 'PREV',
      brandCompleted: (context, _event, { state }) =>
        Boolean(context.dismissedSteps?.brand) && (state.event?.type as string) !== 'PREV',
    },
    services: {
      saveNonprofitData: makeSaveNonprofitData(),
      saveNonprofitDataWithTimestamp: makeSaveNonprofitData(true),
    },
  }
);
